<template>
  <Multiselect
    :class="{'multiselect-sm': small}"
    v-model="vModel"
    :disabled="disabled"
    :options="groups"
    :custom-label="formatLabel"
    track-by="id"
    select-label=""
    deselect-label=""
    :placeholder="placeholder ?? helper.trans('notification_center.merchant_tg_groups_placeholder')"
    open-direction="bottom"
    :multiple="true"
    :searchable="true"
    :loading="isLoading"
    :internal-search="false"
    :clear-on-select="false"
    :close-on-select="false"
    :options-limit="50"
    :limit="50"
    :show-no-results="true"
    :hide-selected="true"
    @search-change="asyncFind"
  />
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, Prop, PropType} from 'vue'
import {debounce, helper} from "@/service/CommonHelper";
import {reactive, toRefs} from "@vue/reactivity";
import Multiselect from "vue-multiselect";
import {api} from "@/service/APIClient";
import TelegramBroadcastMerchantGroupDTO from "@/models/TelegramBroadcastMerchantGroupDTO";

export default defineComponent({
  emits: ['update:modelValue'],
  components: {
    Multiselect
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    } as Prop<boolean>,
    small: {
      type: Boolean,
      default: true
    } as Prop<boolean>,
    placeholder: {
      type: String,
      default: null
    } as Prop<string|null>,
    modelValue: {
      type: Array as PropType<TelegramBroadcastMerchantGroupDTO[]>,
      default: () => []
    },
  },
  setup(props, {emit}) {
    const state = reactive({
      isLoading: false as boolean,
      groups: [] as TelegramBroadcastMerchantGroupDTO[]
    })

    const formatRoleLabel = (role: string) => {
      const key = `notification_center.merchant_tg_group_role.${role}`;
      const label = helper.trans(key);
      return label !== key ? label : role;
    };

    const formatLabel = (opt: TelegramBroadcastMerchantGroupDTO) => {
      const roles = opt.roles?.length
        ? ` [${opt.roles.map(formatRoleLabel).join(', ')}]`
        : '';
      return `${opt.merchantName} (#${opt.id}, ${opt.tgGroupId})${roles}`;
    }

    const asyncFind = debounce(async (query: string) => {
      state.isLoading = true
      try {
        state.groups = await api.telegramBroadcastMerchantGroups(query ?? '')
      } finally {
        state.isLoading = false
      }
    }, 500);

    onMounted(() => {
      asyncFind('');
    });

    const vModel = computed({
      get(): TelegramBroadcastMerchantGroupDTO[] {
        return props.modelValue ?? []
      },
      set(value: TelegramBroadcastMerchantGroupDTO[]): void {
        emit('update:modelValue', value ?? []);
      },
    });

    return {
      ...toRefs(state),
      vModel,
      asyncFind,
      formatLabel,
      helper,
    }
  }
})
</script>
